/** غرضٌ من أغراض صفحة الحجز — باسمه وسعره إن اختصّ بسعر. */
export interface BookingPurpose {
  name: string;
  price?: number | null;
  /**
   * من كتالوج المبيعات — خدمةٌ أو باقة.
   *
   * غرضٌ يُنتقى من الكتالوج يحمل معرّفَه ووحدتَه، فحين يصير الحجزُ عرضَ
   * سعرٍ خرجت بنودُه كما تخرج من نموذج العرض نفسه: بالخدمة ووحدتها،
   * والباقةُ ببنودها المفكوكة. والمكتوبُ يدويًّا يبقى بندًا بوصفه.
   */
  serviceId?: string | null;
  packageId?: string | null;
  unit?: string | null;
}
/** عنصرٌ في معرض صفحة الحجز — صورةٌ تُرى أو فيديو يُشغَّل. */
export interface BookingMedia {
  kind: "IMAGE" | "VIDEO";
  url: string;
  caption?: string | null;
}
/**
 * وسائل الحجز في صفحة الحجز.
 *
 * اثنتان لا أكثر: أونلاين بمنصّةٍ يُرسَل رابطها، أو حضوريّ في مكانٍ
 * يُذكر عنوانه وموقعه. و«هاتفيّ» ليس اجتماعًا — له تبويب المكالمات.
 */
/**
 * أنواع صفحات الحجز.
 *
 * ثلاثةٌ تصف العمل لا البيانات: استشارةٌ مجانيةٌ بلا مال، واستشارةٌ
 * مدفوعةٌ بأسعار أغراضٍ ومقدَّم، وحجزُ مكانٍ يختار العميل مداه.
 */
export const BOOKING_PAGE_KINDS = [
  "FREE_CONSULT",
  "PAID_CONSULT",
  "PLACE",
] as const;
export type BookingPageKind = (typeof BOOKING_PAGE_KINDS)[number];

/**
 * حقول نموذج العميل التي يُبنى منها نموذج الحجز.
 *
 * هي العناصر الأساسية في بطاقة العميل نفسها — فما يُجمع هنا يُصبّ هناك:
 * الشركة تصير شركةَ العميل المحتمل، والمدينة مدينتَه. والاسمُ والبريد
 * لا يُحذفان: بهما يُرسَل التأكيد ويُربط العميل.
 */
export const GUEST_FIELD_CATALOG = [
  { key: "name", label: "الاسم", input: "text", locked: true },
  { key: "email", label: "البريد الإلكتروني", input: "email", locked: true },
  { key: "phone", label: "رقم الهاتف", input: "tel", locked: false },
  { key: "company", label: "الشركة", input: "text", locked: false },
  { key: "jobTitle", label: "المسمّى الوظيفي", input: "text", locked: false },
  { key: "city", label: "المدينة", input: "text", locked: false },
  { key: "address", label: "العنوان", input: "text", locked: false },
  { key: "note", label: "موضوع الحجز", input: "textarea", locked: false },
] as const;
export type GuestFieldKey = (typeof GUEST_FIELD_CATALOG)[number]["key"];

/**
 * أنواع إجابة السؤال الحرّ — كما تُرى في أنواع الحقول في النظام.
 *
 * عناصرُ الفهرس نوعُها ثابت (البريد بريدٌ والهاتف هاتف)، أما السؤال الحرّ
 * فيختار صاحبُ الصفحة نوعَه من هذه القائمة. والاختيارُ — واحدًا أو
 * متعدّدًا أو منسدلًا — يحتاج خياراتٍ تُكتب معه، والملفُّ يُحمَل في
 * الإجابة نفسها `data:` كما تُحمَل صورُ الصفحة.
 */
export const GUEST_INPUT_KINDS = [
  "text",
  "textarea",
  "number",
  "money",
  "tel",
  "email",
  "url",
  "date",
  "time",
  "datetime",
  "select",
  "multiselect",
  "boolean",
  "file",
  "dropdown",
] as const;
export type GuestInputKind = (typeof GUEST_INPUT_KINDS)[number];
export const GUEST_INPUT_KIND_LABELS: Readonly<Record<GuestInputKind, string>> =
  {
    text: "نصٌّ قصير",
    textarea: "نصٌّ طويل",
    number: "رقم",
    money: "مبلغ مالي",
    tel: "هاتف",
    email: "بريد إلكتروني",
    url: "رابط",
    date: "تاريخ",
    time: "وقت",
    datetime: "تاريخ ووقت",
    select: "اختيار واحد",
    multiselect: "اختيار متعدّد",
    boolean: "نعم / لا",
    file: "رفع ملف",
    dropdown: "قائمة منسدلة",
  };
/** الأنواع التي تحتاج خياراتٍ تُكتب معها. */
export const GUEST_INPUT_WITH_OPTIONS: readonly GuestInputKind[] = [
  "select",
  "multiselect",
  "dropdown",
];

/** إجابةُ حقلٍ: نصٌّ، أو ملفٌّ باسمه ومحتواه. */
export type BookingAnswer = string | { name: string; url: string };
/** أقصى حجمٍ لملفٍّ يُرفع في الحجز — بايتات، قبل التحويل إلى `data:`. */
export const MAX_BOOKING_FILE_BYTES = 1_000_000;

export interface BookingGuestField {
  /** مفتاحٌ من الفهرس، أو `q_…` لسؤالٍ حرّ. */
  key: string;
  label: string;
  required: boolean;
  /** نوع الإجابة — للسؤال الحرّ؛ وعناصرُ الفهرس نوعُها من الفهرس. */
  input?: GuestInputKind;
  /** خيارات الاختيار — للأنواع التي تحتاجها. */
  options?: string[];
}

/** الافتراض حين لا يبني صاحبُ الصفحة نموذجًا: ما كان قبل البنّاء. */
export const DEFAULT_GUEST_FIELDS: readonly BookingGuestField[] = [
  { key: "name", label: "الاسم", required: true },
  { key: "email", label: "البريد الإلكتروني", required: true },
  { key: "phone", label: "رقم الهاتف", required: false },
  { key: "note", label: "موضوع الحجز", required: false },
];

/** شرطٌ إضافي: نصٌّ وحده، أو نصٌّ بتكلفةٍ نسبةً أو مبلغًا ثابتًا. */
export const EXTRA_TERM_KINDS = ["TEXT", "PERCENT", "FIXED"] as const;
export type ExtraTermKind = (typeof EXTRA_TERM_KINDS)[number];
export const EXTRA_TERM_KIND_LABELS: Readonly<Record<ExtraTermKind, string>> = {
  TEXT: "نصٌّ فقط",
  PERCENT: "تكلفة إضافية — نسبة مئوية",
  FIXED: "تكلفة إضافية — مبلغ ثابت",
};
export interface BookingExtraTerm {
  title: string;
  kind: ExtraTermKind;
  /** النسبة أو المبلغ — لا معنى له مع النصّ. */
  value: number | null;
}

/** نصُّ التكلفة كما يُقرأ: «+١٠٪» أو «+٥٠٠ ج.م» — وفارغٌ للنصّ. */
export function extraTermCost(t: BookingExtraTerm, currency = "ج.م"): string {
  if (t.kind === "TEXT" || t.value == null) return "";
  return t.kind === "PERCENT"
    ? `+${t.value}%`
    : `+${t.value.toLocaleString("en-US")} ${currency}`;
}

/**
 * المدّة كما تُقال: «ساعة ونصف» لا «٩٠ دقيقة».
 *
 * الدقائقُ تُقرأ حتى الساعة، وبعدها تُقال بالساعات وكسورها الشائعة —
 * ربعًا ونصفًا وثلاثة أرباع — وما شذّ يُقال «ساعتان و٢٠ دقيقة». والعددُ
 * بالأرقام كما يُكتب في سائر النظام، والمعدودُ بقواعده: ساعةٌ وساعتان
 * وثلاثُ ساعات وإحدى عشرة ساعة.
 */
export function durationLabel(min: number): string {
  const m = Math.max(0, Math.round(min));
  const minutes = (n: number) =>
    n === 1
      ? "دقيقة"
      : n === 2
        ? "دقيقتان"
        : n <= 10
          ? `${n} دقائق`
          : `${n} دقيقة`;
  if (m < 60) return minutes(m);
  const h = Math.floor(m / 60);
  const r = m % 60;
  const hours =
    h === 1
      ? "ساعة"
      : h === 2
        ? "ساعتان"
        : h <= 10
          ? `${h} ساعات`
          : `${h} ساعة`;
  if (r === 0) return hours;
  if (r === 15) return `${hours} وربع`;
  if (r === 30) return `${hours} ونصف`;
  if (r === 45) return `${hours} وثلاثة أرباع`;
  return `${hours} و${minutes(r)}`;
}

/**
 * شكلُ صفحة الحجز: عاديةٌ أو عرض.
 *
 * العاديةُ صفحةُ هبوطٍ تعرّف وتحجز. والعرضُ (Show) صفحةٌ تُقرأ فصلًا
 * فصلًا كإصدارات المنتجات الكبرى: عنوانٌ ضخم، وأرقامٌ بارزة، وفصولٌ
 * لكلٍّ عنوانُه ونصُّه وصورتُه ونقاطُه، وأسئلةٌ شائعة — ثم الحجزُ نفسه.
 */
export const BOOKING_LAYOUTS = ["CLASSIC", "SHOW"] as const;
export type BookingLayout = (typeof BOOKING_LAYOUTS)[number];
export const BOOKING_LAYOUT_LABELS: Readonly<Record<BookingLayout, string>> = {
  CLASSIC: "صفحة عادية",
  SHOW: "صفحة عرض",
};

export interface BookingShowChapter {
  id: string;
  /** سطرٌ صغير فوق العنوان — «الإضاءة» أو «الفصل الأول». */
  kicker: string;
  title: string;
  body: string;
  media: BookingMedia | null;
  /** نقاطٌ مختصرة تحت النصّ. */
  bullets: string[];
}
export interface BookingShowStat {
  value: string;
  label: string;
}
export interface BookingShowFaq {
  q: string;
  a: string;
}
export interface BookingShow {
  theme: "light" | "dark";
  kicker: string;
  headline: string;
  subheadline: string;
  ctaLabel: string;
  stats: BookingShowStat[];
  chapters: BookingShowChapter[];
  faq: BookingShowFaq[];
}
export const DEFAULT_BOOKING_SHOW: BookingShow = {
  theme: "dark",
  kicker: "",
  headline: "",
  subheadline: "",
  ctaLabel: "احجز الآن",
  stats: [],
  chapters: [],
  faq: [],
};

/** محتوى العرض كما يُقرأ — ما نقص أُكمل من الافتراض، وما شذّ أُسقط. */
export function showOf(raw: unknown): BookingShow {
  const r =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const str = (v: unknown, max = 400) =>
    typeof v === "string" ? v.slice(0, max) : "";
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  const media = (v: unknown): BookingMedia | null => {
    if (!v || typeof v !== "object") return null;
    const m = v as { kind?: unknown; url?: unknown; caption?: unknown };
    if (typeof m.url !== "string" || !m.url) return null;
    return {
      kind: m.kind === "VIDEO" ? "VIDEO" : "IMAGE",
      url: m.url,
      caption: typeof m.caption === "string" ? m.caption : null,
    };
  };
  return {
    theme: r.theme === "light" ? "light" : "dark",
    kicker: str(r.kicker, 80),
    headline: str(r.headline, 160),
    subheadline: str(r.subheadline, 400),
    ctaLabel: str(r.ctaLabel, 40) || DEFAULT_BOOKING_SHOW.ctaLabel,
    stats: arr(r.stats)
      .map((x) =>
        x && typeof x === "object"
          ? {
              value: str((x as { value?: unknown }).value, 24),
              label: str((x as { label?: unknown }).label, 60),
            }
          : null,
      )
      .filter((x): x is BookingShowStat => Boolean(x && x.value))
      .slice(0, 6),
    chapters: arr(r.chapters)
      .map((x, i) => {
        if (!x || typeof x !== "object") return null;
        const c = x as Record<string, unknown>;
        return {
          id: str(c.id, 40) || `c${i}`,
          kicker: str(c.kicker, 80),
          title: str(c.title, 120),
          body: str(c.body, 2000),
          media: media(c.media),
          bullets: arr(c.bullets)
            .map((b) => str(b, 160))
            .filter(Boolean)
            .slice(0, 8),
        };
      })
      .filter((x): x is BookingShowChapter => Boolean(x && x.title))
      .slice(0, 12),
    faq: arr(r.faq)
      .map((x) =>
        x && typeof x === "object"
          ? {
              q: str((x as { q?: unknown }).q, 200),
              a: str((x as { a?: unknown }).a, 1000),
            }
          : null,
      )
      .filter((x): x is BookingShowFaq => Boolean(x && x.q))
      .slice(0, 12),
  };
}

export const BOOKING_MODES = ["ONLINE", "ONSITE"] as const;
export type BookingMode = (typeof BOOKING_MODES)[number];

export const BOOKING_MODE_LABELS: Readonly<Record<BookingMode, string>> = {
  ONLINE: "أونلاين",
  ONSITE: "حضوريًّا",
};

/**
 * صفحات الحجز.
 *
 * رابطٌ يُرسَل إلى عميل فيختار موعدًا من الأوقات الشاغرة بلا حساب ولا
 * تسجيل دخول. يحلّ محلّ ثلاث رسائل ذهابًا وإيابًا: «متى يناسبك؟» «الثلاثاء»
 * «الثلاثاء مشغول، الأربعاء؟».
 *
 * ## الصفحة تكشف الفراغ لا المحتوى
 *
 * الزائر يرى «هذا الوقت شاغر» ولا يرى ما يملؤه. عناوين المواعيد أسماءُ
 * عملاء ومواضيعُ صفقات، وكشفها لكل من يملك الرابط تسريبٌ لدفتر مواعيد
 * الشركة كاملًا.
 *
 * ## والخادم لا يصدّق الوقت المُرسل
 *
 * الزائر يرسل وقتًا اختاره، والخادم يعيد بناء الأوقات الشاغرة ويتحقّق أن
 * المرسَل واحدٌ منها. من يرسل طلبًا مباشرًا يستطيع أن يختار الثالثة فجرًا
 * أو موعدًا محجوزًا أو نصف ساعة بين موعدين — ولا يحمي منها إلا إعادة
 * الحساب.
 */
import type { Labelled } from "./common.js";

/* ==========================================================================
   الحدود
   ========================================================================== */

/** مُدد الموعد المتاحة — قائمة مغلقة لا حقلٌ حر. */
export const SLOT_MINUTES = [15, 30, 45, 60, 90, 120] as const;
export type SlotMinutes = (typeof SLOT_MINUTES)[number];

/**
 * أقصى مهلة إشعار بالساعات.
 *
 * «لا تحجز قبل ٤٨ ساعة» معقول؛ و«قبل شهر» يعني صفحةً لا تُحجز. والحدّ
 * يمنع إعدادًا يُبطل الصفحة من حيث لا يدري صاحبها.
 */
export const MAX_NOTICE_HOURS = 168;

/** أقصى مدى مفتوح للحجز بالأيام. */
export const MAX_HORIZON_DAYS = 120;

/**
 * أقصى عدد حجوزات لصفحة في اليوم الواحد.
 *
 * المسار عامٌّ يكتب في قاعدة بيانات شركة. وبلا حدٍّ يستطيع واحدٌ أن يملأ
 * تقويم مندوبٍ لشهر في دقائق، فيصير الرابط بابًا مفتوحًا لا خدمة.
 */
export const MAX_BOOKINGS_PER_DAY = 20;

/** أقصى ما يحجزه بريدٌ واحد على الصفحة نفسها ضمن المدى المفتوح. */
export const MAX_BOOKINGS_PER_EMAIL = 3;

/** أقصى عدد أيام تُطلب أوقاتها في نداءٍ واحد. */
export const MAX_SLOT_DAYS = 31;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface BookingPageView {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  calendarId: string;
  calendarName: string | null;
  ownerId: string | null;
  ownerName: string | null;
  durationMin: number;
  bufferMin: number;
  /** نافذة ساعات تخصّ الصفحة ("HH:mm") — فارغةً تأخذ ساعات التقويم. */
  windowStart: string | null;
  windowEnd: string | null;
  noticeHours: number;
  horizonDays: number;
  /** ما يختاره الحاجز: أغراضٌ ووسائل، ومكانُ الحضوريّ وسعرُه المُعلَن. */
  purposes: BookingPurpose[];
  modes: string[];
  locationText: string | null;
  locationMapsUrl: string | null;
  price: number | null;
  /** كيف تُقاس المدّة، وسقفُها، ووحدةُ السعر، ونسبةُ المقدَّم. */
  durationMode: string;
  maxDurationMin: number;
  /** عدد الحجوزات المتزامنة على الصفحة. */
  capacity: number;
  /** نوع الصفحة في العمل — مجانية أو مدفوعة أو مكان. */
  kind: BookingPageKind;
  /** شكلُها: عادية أو عرض — ومحتوى العرض. */
  layout: BookingLayout;
  show: BookingShow;
  /** سياسة الإلغاء والاسترداد. */
  cancellationPolicy: string | null;
  /** نموذج العميل كما بُني — مرتّبًا. */
  guestFields: BookingGuestField[];
  /** الشروط الإضافية بتكلفتها. */
  extraTerms: BookingExtraTerm[];
  priceUnitMin: number;
  depositPercent: number;
  priceNote: string | null;
  media: BookingMedia[];
  createLead: boolean;
  isActive: boolean;
  /** كم حُجز عليها — رقمٌ يقول إن الرابط يعمل. */
  bookedCount: number;
  createdAt: string;
}

/** ما يراه الزائر: تعريفٌ بلا معرّفات داخلية ولا عناوين مواعيد. */
export interface PublicBookingPage {
  slug: string;
  name: string;
  description: string | null;
  tenantName: string;
  /** شعار الشركة data: — الصفحة واجهةُ الشركة أمام عميلها. */
  logoUrl: string | null;
  /** تواصلُ الشركة — في ذيل الصفحة، لمن أراد أن يسأل قبل أن يحجز. */
  tenantPhone: string | null;
  tenantEmail: string | null;
  tenantAddress: string | null;
  /** مفتاحُ الدولة الافتراضي — يُهيَّأ به حقلُ الهاتف في النموذج. */
  defaultCountryCode: string;
  /** اللون الأساسي من هوية الشركة. */
  brandColor: string | null;
  hostName: string | null;
  durationMin: number;
  /** أغراضٌ يختار منها الحاجز — فارغةً تسقط الخطوة. */
  purposes: BookingPurpose[];
  /** وسائل الحجز المتاحة على هذه الصفحة. */
  modes: string[];
  /** مكان الحجز الحضوريّ — يظهر حين تُتاح الوسيلة. */
  locationText: string | null;
  locationMapsUrl: string | null;
  /** سعرٌ مُعلَن وملاحظتُه — عرضًا لا تحصيلًا. */
  price: number | null;
  priceNote: string | null;
  /** المدّة: ثابتةٌ أو مدًى يختاره الحاجز بسقفه ووحدة سعره. */
  durationMode: string;
  maxDurationMin: number;
  /** كم حجزًا يقع في الوقت الواحد — يُعرض ليعرف الحاجز أنه لا يستأثر. */
  capacity: number;
  kind: BookingPageKind;
  layout: BookingLayout;
  show: BookingShow;
  cancellationPolicy: string | null;
  /** حقول النموذج التي يملؤها الحاجز — مرتّبةً بإلزامها. */
  guestFields: BookingGuestField[];
  extraTerms: BookingExtraTerm[];
  priceUnitMin: number;
  depositPercent: number;
  /** معرض الصفحة — صورٌ وفيديو تُرى قبل الحجز. */
  media: BookingMedia[];
  noticeHours: number;
  horizonDays: number;
}

export interface BookingSlot {
  /** بداية الموعد بصيغة ISO. */
  startAt: string;
  endAt: string;
}

export interface BookingDay {
  /** اليوم بصيغة `YYYY-MM-DD` بتوقيت الخادم. */
  date: string;
  slots: BookingSlot[];
}

export interface BookingResult {
  ok: true;
  startAt: string;
  endAt: string;
  hostName: string | null;
  tenantName: string;
  /** قيمة الحجز كما حُسبت، والمقدَّر مقدَّمًا منها. */
  amount?: number | null;
  depositAmount?: number | null;
}

/* ==========================================================================
   توليد الأوقات
   ========================================================================== */

export interface SlotWindow {
  /** أيام العمل بمفاتيحها: sat، sun، … */
  workingDays: readonly string[];
  /** `HH:MM` بتوقيت الخادم. */
  workStart: string;
  workEnd: string;
  durationMin: number;
  bufferMin: number;
  capacity: number;
}

/** فترة مشغولة — بدايةٌ ونهاية، بلا عنوان. */
export interface BusySpan {
  startAt: Date;
  endAt: Date;
}

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function minutesOf(hhmm: string): number {
  const [h = "0", m = "0"] = hhmm.split(":");
  return Number(h) * 60 + Number(m);
}

/** اليوم بصيغة `YYYY-MM-DD` بتوقيت الخادم لا بـ`toISOString` الذي يزيحه. */
export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * الأوقات الشاغرة ليومٍ واحد.
 *
 * دالة خالصة يستعملها الخادم مرتين: مرةً ليعرض الأوقات، ومرةً ليتحقّق أن
 * ما اختاره الزائر واحدٌ منها. ولو حُسبت بمنطقين لقَبِل الخادم وقتًا لم
 * يعرضه، أو رفض وقتًا عرضه بنفسه.
 *
 * والخطوة هي المدة زائد الفاصل: مواعيد متلاصقة بلا فاصل تعني مندوبًا يخرج
 * من مكالمة ليدخل أخرى في الثانية نفسها.
 */
export function slotsForDay(
  day: Date,
  window: SlotWindow,
  busy: readonly BusySpan[],
  notBefore: Date,
  notAfter: Date,
): BookingSlot[] {
  const key = DAY_KEYS[day.getDay()];
  if (!key || !window.workingDays.includes(key)) return [];

  const step = window.durationMin + window.bufferMin;
  if (step <= 0) return [];

  const openMin = minutesOf(window.workStart);
  const closeMin = minutesOf(window.workEnd);
  if (closeMin <= openMin) return [];

  const slots: BookingSlot[] = [];

  for (let m = openMin; m + window.durationMin <= closeMin; m += step) {
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);
    start.setMinutes(m);

    const end = new Date(start.getTime() + window.durationMin * 60_000);

    /* المهلة والمدى: ما قبلهما أو بعدهما ليس معروضًا أصلًا. */
    if (start < notBefore || start > notAfter) continue;

    /*
     * السعة تُحترم كما في تقاويم الموارد.
     *
     * قاعةٌ سعتها اثنان تقبل حجزين متزامنين، وصفحةُ حجزٍ عليها تعرض الوقت
     * شاغرًا حتى يمتلئ — لا حتى يُحجز أوّل مرة.
     */
    const overlapping = busy.filter(
      (b) => b.startAt < end && b.endAt > start,
    ).length;
    if (overlapping >= window.capacity) continue;

    slots.push({ startAt: start.toISOString(), endAt: end.toISOString() });
  }

  return slots;
}

/* ==========================================================================
   العرض
   ========================================================================== */

export const BOOKING_STATE_DEFS: readonly Labelled<"active" | "paused">[] = [
  { key: "active", label: "يستقبل حجوزات", kind: "success" },
  { key: "paused", label: "موقوفة", kind: "neutral" },
] as const;

/** رابط الصفحة العام — يُبنى في مكان واحد فلا ينحرف بين شاشة وأخرى. */
export function bookingUrl(
  origin: string,
  tenantSlug: string,
  slug: string,
): string {
  return `${origin.replace(/\/+$/, "")}/book/${tenantSlug}/${slug}`;
}
